import React, { useMemo } from "react";
import Modal from "../../../../../components/Utils/Modal/Modal";
import { UseMutateFunction, useQuery, useQueryClient } from "react-query";
import api from "../../../../../utils/api";
import { Category, EUserCategoryType, PostCategory, PRIVACY_ENUM, ROLES, UserCategory } from "../../../../../utils/api/_type";
import classNames from "classnames";
import Switch from "../../../../../components/Utils/Switch/Switch";
import Divider from "../../../../../components/Utils/Divider/Divider";

import styles from "./AccessModal.module.css";

interface AdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: PostCategory;
  setValue: React.Dispatch<React.SetStateAction<PostCategory>>;
  updateCategory: UseMutateFunction<
    Category,
    unknown,
    {
      id: number;
      body: { privacy?: PRIVACY_ENUM; isAccessPiloted?: boolean };
    },
    unknown
  >;
  createUserCategory: UseMutateFunction<
    UserCategory,
    unknown,
    {
      userId: number;
      categoryId: number;
      body: {
        type?: EUserCategoryType;
        isNotified?: boolean;
        isGranted?: boolean;
      };
    },
    unknown
  >;
  updateUserCategory: UseMutateFunction<
    UserCategory,
    unknown,
    {
      userCategoryId: number;
      body: {
        type?: EUserCategoryType;
        isGranted?: boolean;
      };
    },
    void
  >;
}

export default function AccessModal({
  isOpen,
  onClose,
  category,
  setValue,
  updateCategory,
  createUserCategory,
  updateUserCategory,
}: AdminModalProps) {
  const queryClient = useQueryClient();
  const { data: companyUsers } = useQuery("company_users", api.companies.getCompanyUsers);

  const { data: userCategories } = useQuery("user_categories", () => api.usercategories.getUserCategoriesByCategory(category.id as number), {
    onSuccess: (data) => {},
    enabled: !!category.id,
  });

  const userCategoriesAdmin = useMemo(() => {
    return userCategories?.filter((uc) => uc.type === "admin");
  }, [userCategories]);

  const adminUsers = useMemo(() => {
    return (
      companyUsers
        // on enlève lesuser avec role COMPANY_OWNER et COMPANY_ADMIN car tjr admin
        ?.filter(
          (user) =>
            user.roles.includes(ROLES.ROLE_APPLICATION_ADMIN) &&
            !user.roles.includes(ROLES.ROLE_COMPANY_OWNER) &&
            !user.roles.includes(ROLES.ROLE_COMPANY_ADMIN)
        )
        .sort((a, b) => (userCategoriesAdmin?.find((uc) => uc.user.id === a.id) ? -1 : 1))
    );
  }, [companyUsers, userCategoriesAdmin]);

  const userCategoriesUser = useMemo(() => {
    return userCategories?.filter((uc) => uc.type === "user");
  }, [userCategories]);

  const sortedCompanyUsers = useMemo(() => {
    return (
      companyUsers
        // on filtre les non users
        ?.filter(
          (user) =>
            !user.roles.includes(ROLES.ROLE_APPLICATION_ADMIN) &&
            !user.roles.includes(ROLES.ROLE_COMPANY_OWNER) &&
            !user.roles.includes(ROLES.ROLE_COMPANY_ADMIN)
        )
        .sort((a, b) => {
          const aUserCategory = userCategoriesUser?.find((uc) => uc.user.id === a.id);
          return aUserCategory?.isGranted ? -1 : 1;
        })
    );
  }, [companyUsers, userCategoriesUser]);

  // Fonction pour gérer les permissions temporaires en mode création
  function updateUserCategoryFromQueriesData(userId: number, body: { type?: EUserCategoryType; isGranted?: boolean }) {
    queryClient.setQueryData<UserCategory[] | undefined>("user_categories", (old: UserCategory[] | undefined) => {
      if (!old) old = [];

      const existingUserCategory = old.find((uc) => uc.user.id === userId);

      if (existingUserCategory) {
        return old.map((uc) => (uc.user.id === userId ? { ...uc, ...body } : uc));
      } else {
        // Créer une entrée temporaire pour le mode création
        const user = companyUsers?.find((u) => u.id === userId);
        if (user) {
          const newUserCategory: UserCategory = {
            id: userId, // Utiliser l'ID utilisateur comme ID temporaire
            user: { id: userId, firstname: user.firstname, lastname: user.lastname },
            category: { id: category.id || -1 },
            type: body.type || EUserCategoryType.USER,
            isNotified: false,
            isGranted: false,
          };
          return [newUserCategory, ...old];
        }
      }
      return old;
    });
  }

  return (
    <Modal title="Gestion des permissions" autoWidth isOpen={isOpen} onClose={onClose} width="500px">
      <div className={styles.accessModal}>
        <div>
          <div className={styles.privacySwitch}>
            <Switch
              checked={category.privacy === PRIVACY_ENUM.PRIVATE}
              onClick={() => {
                const newPrivacy = category.privacy === PRIVACY_ENUM.PUBLIC ? PRIVACY_ENUM.PRIVATE : PRIVACY_ENUM.PUBLIC;
                if (category.id) {
                  updateCategory({
                    id: category.id as number,
                    body: { privacy: newPrivacy },
                  });
                } else {
                  // Mode création - mise à jour temporaire
                  setValue((prev) => ({ ...prev, privacy: newPrivacy }));
                }
              }}
              className={styles.switch}
            />
            <span className={styles.switchLabel}>Permissions manuelles</span>
          </div>
          <div className={styles.adminUsers}>
            {!!adminUsers ? (
              adminUsers.map((user, i) => (
                <>
                  <div key={user.id} className={classNames(styles.adminUser, i !== adminUsers?.length - 1 ? styles.divider : "")}>
                    <div className={styles.adminUserContent}>
                      <div>
                        {user.firstname} {user.lastname}
                      </div>
                    </div>
                    <Switch
                      className={styles.itemSwitch}
                      checked={!!userCategoriesAdmin?.find((uc) => uc.user.id === user.id)}
                      onClick={() => {
                        if (category.id) {
                          // Mode édition - requêtes directes
                          if (userCategoriesAdmin?.find((uc) => uc.user.id === user.id)) {
                            updateUserCategory({
                              userCategoryId: userCategoriesAdmin?.find((uc) => uc.user.id === user.id)?.id as number,
                              body: { type: EUserCategoryType.USER },
                            });
                          } else {
                            createUserCategory({ userId: user.id, categoryId: category.id as number, body: { type: EUserCategoryType.ADMIN } });
                          }
                        } else {
                          // Mode création - gestion temporaire
                          if (userCategoriesAdmin?.find((uc) => uc.user.id === user.id)) {
                            updateUserCategoryFromQueriesData(user.id, { type: EUserCategoryType.USER });
                          } else {
                            updateUserCategoryFromQueriesData(user.id, { type: EUserCategoryType.ADMIN });
                          }
                        }
                      }}
                    />
                  </div>
                </>
              ))
            ) : (
              <span>Aucun administrateur trouvé</span>
            )}
          </div>
        </div>

        <Divider variant="gray" />

        <div>
          <div className={styles.privacySwitch}>
            <Switch
              checked={category.isAccessPiloted}
              onClick={() => {
                const newIsAccessPiloted = !category.isAccessPiloted;
                if (category.id) {
                  updateCategory({
                    id: category.id as number,
                    body: { isAccessPiloted: newIsAccessPiloted },
                  });
                } else {
                  // Mode création - mise à jour temporaire
                  setValue((prev) => ({ ...prev, isAccessPiloted: newIsAccessPiloted }));
                }
              }}
              className={styles.switch}
            />
            <span className={styles.switchLabel}>Visibilité manuelle</span>
          </div>
          <div className={styles.adminUsers}>
            {!!sortedCompanyUsers ? (
              sortedCompanyUsers.map((cu, i) => (
                <>
                  <div key={cu.id} className={classNames(styles.adminUser, i !== sortedCompanyUsers?.length - 1 ? styles.divider : "")}>
                    <div className={styles.adminUserContent}>
                      <div>
                        {cu.firstname} {cu.lastname}
                      </div>
                    </div>
                    <Switch
                      className={styles.itemSwitch}
                      checked={userCategoriesUser?.find((uc) => uc.user.id === cu.id)?.isGranted}
                      onClick={() => {
                        if (category.id) {
                          // Mode édition - requêtes directes
                          if (userCategoriesUser?.find((uc) => uc.user.id === cu.id)) {
                            updateUserCategory({
                              userCategoryId: userCategoriesUser?.find((uc) => uc.user.id === cu.id)?.id as number,
                              body: { isGranted: !userCategoriesUser?.find((uc) => uc.user.id === cu.id)?.isGranted },
                            });
                          } else {
                            createUserCategory({ userId: cu.id, categoryId: category.id as number, body: { isGranted: true } });
                          }
                        } else {
                          // Mode création - gestion temporaire
                          const currentUserCategory = userCategoriesUser?.find((uc) => uc.user.id === cu.id);
                          const newGrantedState = !currentUserCategory?.isGranted;
                          updateUserCategoryFromQueriesData(cu.id, { isGranted: newGrantedState });
                        }
                      }}
                    />
                  </div>
                </>
              ))
            ) : (
              <span>Aucun utilisateur trouvé</span>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
